import { isApiVisible, observeApiVisibility } from './api-visibility';
// Opt-in, read-only server pagination for API grids and latest-response loading
// for API info panels. Existing forms keep their original loading behavior.
const read = (object, path) => String(path || '').split('.').filter(Boolean)
  .reduce((value, part) => value == null ? undefined : value[part], object);
const clone = value => JSON.parse(JSON.stringify(value));
const errorText = error => error?.response?.data?.message || error?.message || 'Die Statistik konnte nicht geladen werden.';

export function installServerPagination(component, kind) {
  if (component.component.serverPagination !== true && component.component.latestResponseOnly !== true) return;
  const info = kind === 'info';
  const edit = kind === 'edit';
  let disposed = false, version = 0, activeSignature = null, publishedSignature = null, filterSignature = null;
  let total = 0, rootHandler = null, timer = null, rendering = false;
  let searchTimer = null, requestedSearch = '', stopVisibility = null;
  const lazy = /^\/?api\/admin\/statistics\//.test(component.apiConfig.loadEndpoint || '');
  const visible = () => !lazy || isApiVisible(component);
  const loadName = info ? 'loadApiInfo' : edit ? 'loadApiRows' : 'loadRows';
  const renderName = info ? 'redraw' : edit ? 'renderUnifiedTable' : 'renderRows';
  const originalRender = component[renderName].bind(component);
  const originalAttach = component.attach.bind(component);
  const originalDestroy = component.destroy.bind(component);
  const originalInit = component.init ? component.init.bind(component) : null;

  function queryState() {
    const config = component.apiConfig || {};
    const data = component.getResolvedContextData ? component.getResolvedContextData() : component.getFormData();
    const params = (config.queryParams || []).map(param => [param.name, read(data, param.path), param.required]);
    return { config, params };
  }
  function requestState() {
    const { config, params } = queryState();
    const filters = JSON.stringify(params);
    if (filterSignature !== null && filterSignature !== filters) component._currentPage = 1;
    filterSignature = filters;
    if (params.some(([,value,required]) => required && (value == null || value === ''))) return null;
    const query = params.filter(([,value]) => value != null && value !== '').map(([name,value]) => [name,value]);
    if (!info) query.push(['page', component._currentPage || 1], ['pageSize',component.getPageSize()],
      ['sort',component._sortField || component.component.defaultSortField || ''], ['direction',component._sortDirection || 'asc'],
      ['search',component._searchValue || '']);
    const endpoint = config.loadEndpoint + (config.loadEndpoint.includes('?') ? '&' : '?') +
      query.map(([name,value]) => encodeURIComponent(name) + '=' + encodeURIComponent(value)).join('&');
    return { endpoint, signature: endpoint, config, search: component._searchValue || '' };
  }
  component.shouldLoad = () => !disposed && visible() && !component.isBuilderMode && component.apiConfig.autoLoad !== false && !!requestState();

  function render() {
    rendering = true;
    try { return originalRender(); } finally { rendering = false; }
  }
  function notify() {
    if (component.updateStatus) component.updateStatus();
    // Rebuilding the EditGrid or its controls destroys the focused search input.
    if (edit && component.refs.serverStatus) {
      const message = component._apiError || (component._apiLoading ? component.component.loadingMessage || 'Daten werden geladen …' : '');
      component.refs.serverStatus.innerHTML = message
        ? `<div class="alert alert-${component._apiError ? 'danger' : 'info'}">${component.escapeHtml(message)}</div>` : '';
    }
    render();
  }
  function assign(rows) {
    if (info) { component._infoData = rows; component.dataValue = rows; }
    else if (edit) {
      component._loadedRows = clone(rows);
      component.dataValue = clone(rows);
      component._actionRowIndex = null; component._editRowIndex = null;
    } else {
      component._rows = rows; component._originalRows = clone(rows); component.dataValue = clone(rows);
      component._dirty = false; component._dirtyRowIndexes.clear();
    }
  }
  component[loadName] = async function(force = false) {
    if (disposed || !visible() || component.isBuilderMode || component.apiConfig.autoLoad === false) return;
    clearTimeout(searchTimer); searchTimer = null;
    const state = requestState();
    if (!state) {
      ++version; activeSignature = null; publishedSignature = null; total = 0;
      component._apiLoading = false; component._apiError = null; assign(info ? null : []); notify(); return;
    }
    if (activeSignature === state.signature || (!force && publishedSignature === state.signature)) return;
    const current = ++version;
    requestedSearch = state.search;
    activeSignature = state.signature;
    component._apiLoading = true; component._apiError = null;
    // Remove previous-filter values while loading so unrelated counts never appear current.
    assign(info ? null : []); total = 0; notify();
    try {
      const response = await component.executeRequest('GET',state.endpoint);
      if (disposed || current !== version || requestState()?.signature !== state.signature) return;
      const body = response.data;
      const data = read(body,state.config.resultPath);
      if (info ? (!data || typeof data !== 'object' || Array.isArray(data)) :
        (!Array.isArray(data) || !Number.isSafeInteger(body.total) || body.total < 0))
        throw new Error('Ungültige Statistikantwort.');
      if (!info) total = body.total;
      if (!info && component._currentPage > Math.max(1,Math.ceil(total/component.getPageSize()))) {
        component._currentPage = Math.max(1,Math.ceil(total/component.getPageSize()));
        return component[loadName](true);
      }
      assign(clone(data));
      publishedSignature = state.signature; component._apiLoadedSignature = component.requestSignature;
    } catch (error) {
      if (disposed || current !== version) return;
      assign(info ? null : []); total = 0; component._apiError = errorText(error);
      // Explicit retry is available; avoid an automatic error/redraw loop.
      publishedSignature = state.signature; component._apiLoadedSignature = component.requestSignature;
    } finally {
      if (!disposed && current === version) {
        activeSignature = null; component._apiLoading = false;
        notify();
      }
    }
  };

  component[renderName] = function(...args) {
    if (!rendering && !info && component.shouldLoad()) {
      const state = requestState();
      if (state && state.signature !== publishedSignature && state.signature !== activeSignature) {
        if (state.search !== requestedSearch) {
          clearTimeout(searchTimer);
          searchTimer = setTimeout(() => component[loadName](false), 300);
        } else component[loadName](false);
      }
    }
    return originalRender(...args);
  };

  if (!info) {
    if (!edit && component.formatColumnCellValue) {
      const originalFormat = component.formatColumnCellValue.bind(component);
      component.formatColumnCellValue = (value,column) => {
        if (column && ['datetime','datetimeUtc'].includes(column.format)) {
          if (value == null || value === '') return column.emptyDisplayLabel || 'Nicht verfügbar';
          const date = new Date(value);
          if (!Number.isNaN(date.getTime())) {
            const utc = column.format === 'datetimeUtc';
            return new Intl.DateTimeFormat('de-DE',{timeZone:utc?'UTC':'Europe/Berlin',
              dateStyle:'short',timeStyle:'short'}).format(date) + (utc?' UTC':'');
          }
        }
        return originalFormat(value,column);
      };
    }
    if (edit) {
      // The read-only table needs no editable row component instances.
      // Formio's EditGrid.setValue rebuilds the parent, destroying in-flight
      // loader state; keep server rows directly in the component data instead.
      component.setValue = (value, flags = {}) => {
        if (flags.fromSubmission && publishedSignature) return false;
        const rows = Array.isArray(value) ? value : [];
        const changed = JSON.stringify(component.dataValue) !== JSON.stringify(rows);
        component.dataValue = clone(rows);
        return changed;
      };
      component.getValue = () => component.dataValue || [];
      component.getFilteredRows = () => (component.dataValue || []).map((row,originalIndex) => ({row,originalIndex}));
      component.getPagedRows = () => {
        const entries = component.getFilteredRows();
        return { filtered:entries,pageRows:entries,pageCount:Math.max(1,Math.ceil(total/component.getPageSize())) };
      };
    } else {
      component.getProcessedEntries = () => (component._rows || []).map((row,originalIndex) => ({row,originalIndex}));
      component.getPagedEntries = entries => entries;
    }
    component.renderPagination = () => {
      const host = edit ? component.refs.pagination : component.refs.paginationRef;
      if (!host) return;
      const page = component._currentPage || 1, pages = Math.max(1,Math.ceil(total/component.getPageSize()));
      host.innerHTML = `<div class="d-flex justify-content-between align-items-center"><span role="status">${total} Einträge · Seite ${page} von ${pages}</span><div>
        <button type="button" class="btn btn-outline-secondary btn-sm mr-2" data-server-page="-1" ${page<=1 || component._apiLoading ? 'disabled' : ''}>Zurück</button>
        <button type="button" class="btn btn-outline-secondary btn-sm" data-server-page="1" ${page>=pages || component._apiLoading ? 'disabled' : ''}>Weiter</button>
        <button type="button" class="btn btn-outline-secondary btn-sm ml-2" data-server-retry ${component._apiLoading ? 'disabled' : ''}>Aktualisieren</button></div></div>`;
      host.querySelectorAll('[data-server-page]').forEach(button => component.addEventListener(button,'click',() => {
        component._currentPage += Number(button.dataset.serverPage);
        if (component.destroyRowEditor) component.destroyRowEditor();
        component[loadName](true);
      }));
      const retry = host.querySelector('[data-server-retry]');
      component.addEventListener(retry,'click',() => component[loadName](true));
    };
  }

  component.attach = function(...args) {
    const attached = originalAttach(...args);
    if (lazy && !disposed && !component.isBuilderMode) {
      if (stopVisibility) stopVisibility();
      stopVisibility = observeApiVisibility(component, () => {
        clearTimeout(timer);
        timer = setTimeout(() => component[loadName](false), 100);
      });
    }
    if (!disposed && !component.isBuilderMode && component.root?.on && !rootHandler) {
      rootHandler = () => {
        clearTimeout(timer);
        timer = setTimeout(() => component[loadName](false),100);
      };
      component.root.on('change',rootHandler);
    }
    return attached;
  };
  component.destroy = function(...args) {
    disposed = true; ++version; clearTimeout(timer); clearTimeout(searchTimer);
    if (stopVisibility) stopVisibility();
    stopVisibility = null;
    if (rootHandler && component.root?.off) component.root.off('change',rootHandler);
    rootHandler = null; activeSignature = null; publishedSignature = null;
    return originalDestroy(...args);
  };
  if (originalInit) component.init = function(...args) {
    disposed = false;
    return originalInit(...args);
  };
}
